import { Group, Text } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useRef, useState, type PointerEvent, type ReactNode } from "react";

// Swipe at least this far left (px, or this share of the row's width) to delete.
const MIN_DISTANCE = 96;
const WIDTH_SHARE = 0.4;
// Movement before deciding whether the finger is scrolling or swiping.
const DECIDE_AFTER = 8;

type Drag = {
  x: number;
  y: number;
  width: number;
  direction: "unknown" | "swipe" | "scroll";
  // Latest offset, kept here too: a fast flick can end before React re-renders.
  dx: number;
};

/**
 * Swipe a row left to delete it, on touch screens only. Vertical movement is left to the
 * browser (`touch-action: pan-y`), so the page still scrolls normally.
 */
export function SwipeToDelete({ onDelete, children }: { onDelete: () => void; children: ReactNode }) {
  const drag = useRef<Drag | null>(null);
  const [dx, setDx] = useState(0);
  const [settling, setSettling] = useState(false);
  const [armed, setArmed] = useState(false);

  const threshold = (width: number) => Math.min(MIN_DISTANCE * 1.5, Math.max(MIN_DISTANCE, width * WIDTH_SHARE));

  const reset = () => {
    drag.current = null;
    setSettling(true);
    setDx(0);
    setArmed(false);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "touch") return;
    drag.current = {
      x: e.clientX,
      y: e.clientY,
      width: e.currentTarget.offsetWidth,
      direction: "unknown",
      dx: 0,
    };
    setSettling(false);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const moveX = e.clientX - d.x;
    const moveY = e.clientY - d.y;
    if (d.direction === "unknown") {
      if (Math.abs(moveX) < DECIDE_AFTER && Math.abs(moveY) < DECIDE_AFTER) return;
      d.direction = Math.abs(moveX) > Math.abs(moveY) ? "swipe" : "scroll";
      if (d.direction === "swipe") {
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          // The pointer already ended; the swipe still works without capture.
        }
      }
    }
    if (d.direction !== "swipe") return;
    const next = Math.min(0, moveX);
    d.dx = next;
    setDx(next);
    const nowArmed = -next >= threshold(d.width);
    if (nowArmed !== armed) {
      setArmed(nowArmed);
      if (nowArmed) navigator.vibrate?.(10);
    }
  };

  const onPointerUp = () => {
    const d = drag.current;
    if (!d || d.direction !== "swipe") return reset();
    if (-d.dx >= threshold(d.width)) {
      // Slide the row out, then delete.
      drag.current = null;
      setSettling(true);
      setDx(-d.width);
      window.setTimeout(onDelete, 180);
    } else {
      reset();
    }
  };

  return (
    <div style={{ position: "relative", overflow: "hidden", borderRadius: 6 }}>
      <Group
        aria-hidden
        justify="flex-end"
        gap={6}
        px="md"
        style={{
          position: "absolute",
          inset: 0,
          background: armed ? "var(--mantine-color-red-7)" : "var(--mantine-color-red-5)",
          color: "white",
          opacity: dx === 0 ? 0 : 1,
        }}
      >
        <IconTrash size={16} />
        <Text size="sm" fw={600}>
          Delete
        </Text>
      </Group>
      <div
        data-testid="swipe-row"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={reset}
        style={{
          position: "relative",
          touchAction: "pan-y",
          transform: `translateX(${dx}px)`,
          transition: settling ? "transform 180ms ease-out" : "none",
          background: "var(--mantine-color-body)",
        }}
      >
        {children}
      </div>
    </div>
  );
}
